import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  StyleSheet,
  View,
  TouchableHighlight,
  TextInput,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Modal,
} from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import DyteImageViewer from './DyteImageViewer';
import DyteFileMessage from './DyteFileMessage';
import { UIConfig } from '../types/ui-config';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { defaultConfig } from '../lib/default-ui-config';
import UserAvatar from 'react-native-user-avatar';
import { GiftedChat, Bubble, IMessage } from 'react-native-gifted-chat';
import DocumentPicker from 'react-native-document-picker';
import { SvgXml } from 'react-native-svg';
import DyteText from './DyteText';
import DyteSpinner from './DyteSpinner';
import { DyteUIContext } from '../lib/context-provider/ui-context';

// ToDo : Polish the UI
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function PinnedButton({ iconPack, props, states }) {
  const { dropDownEnabled, setDropDownEnabled } = states;
  const chat = useDyteSelector((m) => m.chat);
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const pinChatPermission = useDyteSelector(
    (m) => m.self.permissions.pinParticipant
  );
  const styles = StyleSheet.create({
    pinChat: {
      padding: 4,
    },
    hostControlContainer: {
      position: 'absolute',
      top: -40,
      backgroundColor: colors.background[1000],
      borderRadius: 4,
    },
    controls: {
      margin: 4,
      position: 'absolute',
      left: 0,
      top: 0,
      backgroundColor: colors.background[1000],
    },
    options: {
      flex: 1,
      flexDirection: 'row',
      padding: 8,
    },
    optionText: {
      marginLeft: 10,
    },
  });
  return pinChatPermission ? (
    <View>
      <TouchableHighlight
        style={styles.pinChat}
        onPress={() => setDropDownEnabled(!dropDownEnabled)}
      >
        <SvgXml
          xml={(!dropDownEnabled
            ? iconPack.chevron_down
            : iconPack.chevron_up
          ).replace('currentColor', colors.text)}
          width={24}
          height={24}
        />
      </TouchableHighlight>
      {dropDownEnabled && (
        <View style={styles.hostControlContainer}>
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={() => {
              props.currentMessage.pinned
                ? chat.unpin(props.currentMessage.chatID)
                : chat.pin(props.currentMessage.chatID);
            }}
            style={styles.controls}
          >
            <View style={styles.options}>
              <SvgXml
                xml={iconPack.pin.replace('currentColor', colors.text)}
                width={20}
                height={20}
              />
              <DyteText size={'sm'} style={styles.optionText}>
                {props.currentMessage.pinned ? 'Unpin' : 'Pin'}
              </DyteText>
            </View>
          </TouchableHighlight>
        </View>
      )}
    </View>
  ) : null;
}

function ShowPinnedChatButton({ iconPack, pinEnabled, onPinChanged }) {
  const pinnedChatMessages = useDyteSelector((m) => m.chat.pinned);
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  return (
    <TouchableHighlight
      // eslint-disable-next-line react-native/no-inline-styles
      style={{
        backgroundColor: pinEnabled
          ? colors.brand[500]
          : colors.background[700],
        padding: 4,
        marginLeft: 8,
      }}
      onPress={() => onPinChanged()}
    >
      <View
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          flexDirection: 'row',
        }}
      >
        <SvgXml
          xml={iconPack.pin.replace('currentColor', colors.text)}
          width={20}
          height={20}
        />
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <DyteText style={{ marginLeft: 4 }}>
          Pinned Messages ({pinnedChatMessages.length})
        </DyteText>
      </View>
    </TouchableHighlight>
  );
}

function RenderBubble({ iconPack, props, messages }) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dropDownEnabled, setDropDownEnabled] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pinChatPermission = useDyteSelector(
    (m) => m.self.permissions.acceptWaitingRequests
  );
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const closeFullScreen = () => {
    return setStates({
      states: { ...storeStates, activeFullscreen: false },
    });
  };
  const visible =
    storeStates.activeFullscreen &&
    storeStates.fullscreenImageID === props.currentMessage._id;
  const styles = StyleSheet.create({
    fullScreenContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 8,
      backgroundColor: colors.background[900],
    },
    fullscreen: {
      justifyContent: 'center',
      alignItems: 'center',
    },
  });
  const fullscreenImage = messages.filter(
    (im) => im._id === storeStates.fullscreenImageID
  )[0];
  return (
    <View>
      {props.currentMessage.type === 'text' && (
        // eslint-disable-next-line react-native/no-inline-styles
        <View style={{ marginTop: dropDownEnabled ? 40 : 0 }}>
          {/* ToDo: Polish the pinned button UI*/}
          {/* <View
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
              marginRight: props.position === 'right' ? 0 : 60,
              marginLeft: props.position === 'left' ? 0 : 60,
            }}
          >
            <PinnedButton
              iconPack={iconPack}
              props={props}
              states={{ dropDownEnabled, setDropDownEnabled }}
            />
          </View> */}
          {props.currentMessage.pinned && (
            <View
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                position: 'absolute',
                top: -5,
                right: 5,
                zIndex: 1,
                padding: 4,
                borderRadius: 4,
                backgroundColor: colors.background[600],
                marginRight: props.position === 'right' ? 0 : 60,
                marginLeft: props.position === 'left' ? 0 : 60,
              }}
            >
              <SvgXml
                xml={iconPack.pin.replace('currentColor', colors.text)}
                width={20}
                height={20}
              />
            </View>
          )}
          <View>
            <Bubble
              {...props}
              wrapperStyle={{
                left: {
                  backgroundColor: colors.background[800],
                  borderRadius: 8,
                  paddingVertical: 6,
                  paddingHorizontal: 3,
                  // paddingTop: pinChatPermission ? 20 : 0,
                },
                right: {
                  backgroundColor: colors.background[700],
                  borderRadius: 8,
                  paddingVertical: 6,
                  paddingHorizontal: 3,
                  // paddingTop: pinChatPermission ? 20 : 0,
                },
              }}
              textStyle={{
                left: {
                  color: colors.text,
                  fontWeight: '400',
                },
                right: {
                  fontWeight: '400',
                  color: colors.text,
                },
              }}
            />
          </View>
        </View>
      )}
      {props.currentMessage.type === 'image' && (
        <>
          <Modal transparent visible={visible} onRequestClose={closeFullScreen}>
            <View style={styles.fullScreenContainer}>
              <View style={styles.fullscreen}>
                {fullscreenImage && (
                  <DyteImageViewer
                    image={fullscreenImage}
                    _id={props.currentMessage._id}
                  />
                )}
              </View>
            </View>
          </Modal>
          <DyteImageViewer
            image={props.currentMessage}
            _id={props.currentMessage._id}
          />
        </>
      )}
      {props.currentMessage.type === 'file' && (
        <DyteFileMessage message={props.currentMessage} />
      )}
    </View>
  );
}

export default function ({
  meeting,
  // ToDo: Figure out if we need this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  iconPack = defaultIconPack,
  size = 'sm',
  t,
}: {
  meeting: DyteClient;
  config?: UIConfig;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  t?: DyteI18n;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const chatMessages = useDyteSelector((m) => m.chat.messages);
  const [pinEnabled, setPinEnabled] = useState(false);
  const pinnedChatMessages = useDyteSelector((m) => m.chat.pinned);
  const defaultLang = useLanguage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const self = meeting.self;
  const [text, onChangeText] = React.useState('');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const giftedChatRef = useRef<FlatList<IMessage> | null>(null);
  const scrollToBottom = () => {
    if (giftedChatRef.current) {
      giftedChatRef.current.scrollToIndex({ index: 0 });
    }
  };
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    if (pinnedChatMessages.length === 0) setPinEnabled(false);
  }, [pinnedChatMessages.length]);
  let buttonSize = 25;
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      buttonSize = 150;
      break;
  }

  useEffect(() => {
    const msgs = pinEnabled ? [...pinnedChatMessages] : [...chatMessages];
    setMessages(
      msgs.reverse().map((m) => {
        const messageBase = {
          pinned: m.pinned,
          chatID: m.id,
          type: m.type,
          createdAt: m.time,
          _id: msgs.indexOf(m),
          user: {
            _id: m.userId,
            name: m.displayName,
          },
        };
        if (m.type === 'text') {
          return {
            ...messageBase,
            text: m.message,
          };
        }
        if (m.type === 'image') {
          return {
            ...messageBase,
            link: m.link,
          };
        }
        if (m.type === 'file') {
          return {
            ...messageBase,
            name: m.name,
            size: m.size,
            link: m.link,
          };
        }
        return {};
      })
    );
  }, [chatMessages, pinEnabled, pinnedChatMessages]);

  const onTextMessageSend = () => {
    if (messages.length > 0) scrollToBottom();
    meeting.chat.sendTextMessage(text);
    onChangeText('');
  };

  const primaryStyles = StyleSheet.create({
    chat: {
      flexDirection: 'column',
      justifyContent: 'space-between',
      flex: 1,
      width: '100%',
      height: '100%',
    },
    chatContents: {
      flex: 1,
    },
    chatInput: {
      width: '100%',
      height: 100,
      position: 'absolute',
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      paddingTop: 10,
      bottom: 0,
      paddingHorizontal: 15,
      backgroundColor: colors.background[800],
      borderTopColor: colors.background[700],
      borderTopWidth: 2,
    },
    sendButton: {
      height: 40,
      width: 40,
      justifyContent: 'center',
      alignItems: 'center',
    },
    messageContainer: {
      backgroundColor: colors.background[900],
      paddingBottom: 56,
      paddingTop: 4,
    },
    inputBox: {
      color: colors.text,
      paddingHorizontal: 15,
      height: 40,
      borderRadius: 8,
      flex: 1,
    },
    utilityButtons: {
      flex: 1,
      width: '100%',
      maxHeight: 40,
      flexDirection: 'row',
    },
    utilityImage: {
      height: 25,
      width: 25,
      marginLeft: 15,
      justifyContent: 'center',
      alignItems: 'center',
    },
    utilityEmoji: {
      height: 30,
      width: 30,
      marginLeft: 15,
      justifyContent: 'center',
      alignItems: 'center',
    },
    inputContainer: {
      flex: 1,
      flexDirection: 'row',
      maxHeight: 50,
    },
    fileIcon: {
      height: 25,
      marginLeft: 10,
      width: 25,
      justifyContent: 'center',
      alignItems: 'center',
    },
  });

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ ...primaryStyles.chat }}
    >
      {pinnedChatMessages.length > 0 && (
        <ShowPinnedChatButton
          iconPack={iconPack}
          pinEnabled={pinEnabled}
          onPinChanged={() => setPinEnabled(!pinEnabled)}
        />
      )}
      <GiftedChat
        messageContainerRef={giftedChatRef}
        messagesContainerStyle={primaryStyles.messageContainer}
        messages={messages}
        alwaysShowSend
        renderAvatarOnTop
        renderUsernameOnMessage
        isKeyboardInternallyHandled={false}
        timeTextStyle={{
          left: {
            color: colors.text,
          },
          right: {
            color: colors.text,
          },
        }}
        renderAvatar={(props) => (
          <UserAvatar
            name={props.currentMessage.user.name ?? 'TT'}
            bgColor={colors.brand[700]}
          />
        )}
        renderBubble={(props) => {
          return (
            <RenderBubble
              iconPack={iconPack}
              props={props}
              messages={messages}
            />
          );
        }}
        textInputProps={primaryStyles.chatInput}
        renderInputToolbar={() =>
          self.permissions.chatPublic.canSend && (
            <View style={[primaryStyles.chatInput]}>
              <View style={primaryStyles.inputContainer}>
                <TextInput
                  editable={meeting.self.permissions.chatPublic.text}
                  style={primaryStyles.inputBox}
                  placeholder="Message..."
                  placeholderTextColor={colors.text}
                  onChangeText={onChangeText}
                  value={text}
                />
                <TouchableHighlight
                  underlayColor={colors.background[800]}
                  onPress={onTextMessageSend}
                  disabled={!meeting.self.permissions.chatPublic.text}
                >
                  <View style={primaryStyles.sendButton}>
                    <SvgXml
                      xml={iconPack.send.replace('currentColor', colors.text)}
                      width={buttonSize}
                      height={buttonSize}
                    />
                  </View>
                </TouchableHighlight>
              </View>
              {meeting.self.permissions.chatPublic.files && (
                <View style={primaryStyles.utilityButtons}>
                  <TouchableHighlight
                    underlayColor={colors.background[800]}
                    onPress={async () => {
                      try {
                        const res = await DocumentPicker.pickSingle({
                          type: [DocumentPicker.types.allFiles],
                        });
                        const file = {
                          uri: res.uri,
                          name: res.name,
                          size: res.size,
                          type: res.type,
                        };
                        meeting.chat
                          .sendFileMessage(file)
                          .then(() => setUploadingFile(false));
                        setUploadingFile(true);
                      } catch (_err) {}
                    }}
                    style={primaryStyles.fileIcon}
                  >
                    {uploadingFile ? (
                      <DyteSpinner />
                    ) : (
                      <SvgXml
                        xml={iconPack.attach.replace(
                          'currentColor',
                          colors.text
                        )}
                        width={buttonSize}
                        height={buttonSize}
                      />
                    )}
                  </TouchableHighlight>
                  <TouchableHighlight
                    underlayColor={colors.background[800]}
                    onPress={async () => {
                      try {
                        const res = await DocumentPicker.pickSingle({
                          type: [DocumentPicker.types.images],
                        });
                        const image = {
                          uri: res.uri,
                          name: res.name,
                          size: res.size,
                          type: res.type,
                        };
                        meeting.chat
                          .sendImageMessage(image)
                          .then(() => setUploadingImage(false));
                        setUploadingImage(true);
                      } catch (_err) {}
                    }}
                    style={primaryStyles.utilityImage}
                  >
                    {uploadingImage ? (
                      <DyteSpinner />
                    ) : (
                      <SvgXml
                        xml={iconPack.image.replace(
                          'currentColor',
                          colors.text
                        )}
                        width={buttonSize}
                        height={buttonSize}
                      />
                    )}
                  </TouchableHighlight>
                </View>
              )}
            </View>
          )
        }
        user={{
          _id: self.userId,
          name: self.name,
        }}
      />
    </KeyboardAvoidingView>
  );
}
